// src/App.jsx
import React, { useEffect, useRef, useState } from "react";
import { jwtDecode } from "jwt-decode";
import "../css/App.css";
import { useUser } from "./context/userContext";
import { Route, Routes } from "react-router-dom";
import AdminPanel from "../components/admin/AdminPanel";
import MainLayout from "../components/layout/MainLayout";
import i18next from "i18next";
import i18n from "../i18n"

/* ───── helpers ──────────────────────────────────────────────────── */
const looksLikeJwt = (t) => typeof t === "string" && t.split(".").length === 3;

const isExpired = (t) => {
  try {
    const { exp } = jwtDecode(t);
    return Date.now() >= exp * 1000;
  } catch {
    return true;
  }
};

/* ───── main component ───────────────────────────────────────────── */
export default function App() {
  const {
    user,
    setUser,
    setUserId,
    isAdmin,
    setUserAdminStatus,
    setUserName,
    setUserSuperAdminStatus,
  } = useUser();

  const ran = useRef(false);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  /* one-time auth bootstrap */
  useEffect(() => {
    if (ran.current) return;

    const qs = new URLSearchParams(window.location.search);
    const qsToken = qs.get("token");
    const lsToken = localStorage.getItem("userToken");
    const token = qsToken || lsToken;

    if (looksLikeJwt(token) && !isExpired(token)) {
      try {
        const d = jwtDecode(token);
        setUser({ loggedIn: true, ...d });
        setUserId(d.userId);
        setUserName(d.name);
        setUserAdminStatus(d.isAdmin);
        setUserSuperAdminStatus(d.isSuperAdmin);
        localStorage.setItem("userToken", token);
        if (d.language) i18next.changeLanguage(d.language);
      } catch {
        localStorage.removeItem("userToken");
      }
    } else {
      localStorage.removeItem("userToken");
    }

    if (qsToken) {
      qs.delete("token");
      window.history.replaceState({}, "", window.location.pathname);
    }

    ran.current = true;
    setLoading(false);
  }, []);

  /* still checking — show nothing */
  if (loading) return null;

  /* unauthenticated view */
  if (!user?.loggedIn) {
    return (
      <LoginScreen
        email={email}
        setEmail={setEmail}
        message={message}
        setMessage={setMessage}
      />
    );
  }

  /* authenticated view */
  return (
    <Routes>
      <Route path="/" element={<MainLayout />} />
      {isAdmin && <Route path="/admin" element={<AdminPanel />} />}
    </Routes>
  );
}

/* ───── login component ──────────────────────────────────────────── */
function LoginScreen({ email, setEmail, message, setMessage }) {
  const googleLogin = () => {
    const clientId =
      "563104687257-56ml4uf8htddcj8k4kidurkpm0dbapfj.apps.googleusercontent.com";
    const redirectUri = encodeURIComponent(
      process.env.REACT_APP_REDIRECT_URI
    );
    const scope = encodeURIComponent("openid profile email");
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&response_type=code&access_type=offline&prompt=consent`;
  };

  const emailLogin = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_CLIENT_HOME_URL}/auth/send-magic-link`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );
      setMessage(
        res.ok
          ? "Access link sent! Check your email."
          : "Failed to send access link."
      );
    } catch {
      setMessage("Network error, try again.");
    }
  };

  return (
    <div className="AppLogin">
      <div className="login-modal">
        <h2>Welcome to BNB&apos;s Assistant Hub</h2>
        <p>Log in with Google or request an access link.</p>

        <div className="login-container">
          <div className="login-box">
            <h2>BNB Users</h2>
            <button className="login-button" onClick={googleLogin}>
              Login with Google
            </button>
          </div>

          <div className="login-box">
            <h2>External Users</h2>
            <div className="email-login">
              <input
                className="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
              />
              <button className="login-button" onClick={emailLogin}>
                Request Access Link
              </button>
            </div>
            {message && <p className="login-message">{message}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}